import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import {
	Button,
	Flag,
	FlexGap,
	Paragraph,
	PlaybackPauseIcon,
	PlaybackStopIcon,
	Spinner,
} from '@treams/treams-ui';

import {
	BAR_STEP,
	BannerLabel,
	BannerLink,
	BannerWrapper,
	Elapsed,
	PulsingDot,
	RecordIcon,
	formatElapsed,
} from './RecordConversation';
import { useStreamingTranscriptionContext } from './StreamingTranscriptionContext';
import {
	transcriptionLanguageFlagID,
	transcriptionLanguageLabelKey,
} from './TranscriptionLanguageSelect';
import VoiceMemoWaveForm from './VoiceMemoWaveForm';

type Confirming = 'finish' | 'cancel' | null;

/** The recording banner for a streaming (v2) session. */
export function StreamingRecordConversation() {
	const { t } = useTranslation();
	const { state, pause, resume, finish, stopFinishing, discard, cancel, close } =
		useStreamingTranscriptionContext();
	const [confirming, setConfirming] = useState<Confirming>(null);

	const {
		session,
		phase,
		countdown,
		elapsed,
		bars,
		barCount,
		current,
		assemblyStatus,
		failure,
		language,
	} = state;

	const visible = !!session && phase !== 'idle';
	const isRecording = phase === 'recording';
	const isReconnecting = isRecording && assemblyStatus === 'reconnecting';
	const isStopped = phase === 'paused' || phase === 'interrupted';
	const busy = phase === 'pausing' || phase === 'finishing' || phase === 'cancelling';

	const label = session?.label ?? t('transcription.recordConversation');
	const title = session?.targetPath ? (
		<BannerLink to={session.targetPath}>{label}</BannerLink>
	) : (
		<BannerLabel>{label}</BannerLabel>
	);

	const stoppedStatus =
		phase === 'interrupted'
			? t('transcription.streaming.interrupted')
			: failure
				? t(`transcription.streaming.failure.${failure}`)
				: t('transcription.streaming.status.paused');

	const busyLabel =
		phase === 'finishing'
			? t('transcription.streaming.status.finishing')
			: phase === 'cancelling'
				? t('transcription.streaming.status.cancelling')
				: null;

	// Only the newest bars are kept; shift them so the waveform keeps scrolling.
	const hiddenBars = barCount - bars.length;

	return (
		<AnimatePresence>
			{visible && (
				<BannerWrapper
					key="streaming-recording-banner"
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.2 }}
					style={{ overflow: 'hidden' }}
				>
					<FlexGap
						gap={16}
						alignItems={['flex-start', 'flex-start', 'flex-start', 'center']}
						justifyContent="space-between"
						maxWidth="1000px"
						flexGrow={1}
						flexDirection={['column', 'column', 'column', 'row']}
						paddingY="10px"
						paddingX="24px"
					>
						<FlexGap gap={12} alignItems="center">
							{phase === 'starting' && (
								<FlexGap gap={8} alignItems="center">
									<Spinner />
									{title}
								</FlexGap>
							)}

							{countdown !== null && (
								<BannerLabel>
									{t('transcription.startingIn')} {countdown}...
								</BannerLabel>
							)}

							{isRecording && (
								<>
									<PulsingDot />
									{title}
									<Elapsed>&middot; {formatElapsed(elapsed)}</Elapsed>
									{isReconnecting ? (
										<FlexGap gap={8} alignItems="center">
											<Spinner />
											<Elapsed>{t('transcription.streaming.status.notCapturing')}</Elapsed>
										</FlexGap>
									) : (
										<Box
											width="120px"
											height="20px"
											style={{ overflow: 'hidden', position: 'relative' }}
										>
											<motion.div
												animate={{ x: -(barCount * BAR_STEP) }}
												transition={{ duration: 1, ease: 'linear' }}
												style={{
													position: 'absolute',
													left: `calc(100% - ${BAR_STEP}px + ${hiddenBars * BAR_STEP}px)`,
													top: '50%',
													translateY: '-50%',
												}}
											>
												<VoiceMemoWaveForm recording bars={bars} current={current} />
											</motion.div>
										</Box>
									)}
								</>
							)}

							{busy && (
								<FlexGap gap={8} alignItems="center">
									<Spinner />
									{busyLabel ? <BannerLabel>{busyLabel}</BannerLabel> : title}
								</FlexGap>
							)}

							{isStopped && (
								<>
									{title}
									<Elapsed>&middot; {formatElapsed(elapsed)}</Elapsed>
									<Elapsed>{stoppedStatus}</Elapsed>
								</>
							)}
						</FlexGap>

						<FlexGap gap={8} alignItems="center">
							{confirming ? (
								<FlexGap
									gap={8}
									alignItems={['flex-start', 'flex-start', 'center']}
									flexDirection={['column', 'column', 'row']}
								>
									<Paragraph small style={{ whiteSpace: 'nowrap' }}>
										{confirming === 'finish'
											? t('transcription.confirmStop')
											: t('transcription.streaming.cancelConfirm')}
									</Paragraph>
									<FlexGap gap={8} alignItems="center">
										<Button
											title={
												confirming === 'finish'
													? t('transcription.confirmStopYes')
													: t('transcription.confirmCancelYes')
											}
											type={confirming === 'finish' ? 'ai' : 'destructive'}
											small
											onClick={() => {
												const action = confirming;
												setConfirming(null);
												void (action === 'finish' ? finish() : discard());
											}}
										/>
										<Button
											title={
												confirming === 'finish'
													? t('transcription.confirmStopNo')
													: t('transcription.confirmCancelNo')
											}
											type="transparent"
											small
											onClick={() => setConfirming(null)}
										/>
									</FlexGap>
								</FlexGap>
							) : (
								<>
									{isStopped && language && (
										<Box title={t(transcriptionLanguageLabelKey(language))}>
											<Flag languageID={transcriptionLanguageFlagID(language)} />
										</Box>
									)}
									{(isRecording || isStopped) && (
										<Button
											title={t('common.cancel')}
											type="transparent"
											small
											onClick={() => setConfirming('cancel')}
										/>
									)}
									{isRecording && (
										<Button
											title={t('transcription.streaming.pause')}
											type="secondary"
											small
											icon={<PlaybackPauseIcon />}
											onClick={() => {
												void pause();
											}}
										/>
									)}
									{isStopped && (
										<Button
											title={t('transcription.streaming.continue')}
											type="ai"
											small
											icon={<RecordIcon />}
											onClick={() => {
												void resume();
											}}
										/>
									)}
									{(isRecording || isStopped) && (
										<Button
											title={t('transcription.streaming.finish')}
											type={isRecording ? 'ai' : 'secondary'}
											small
											icon={<PlaybackStopIcon />}
											onClick={() => setConfirming('finish')}
										/>
									)}
									{(phase === 'starting' || phase === 'countdown') && (
										<Button
											title={t('common.cancel')}
											type="transparent"
											small
											onClick={() => {
												void cancel();
											}}
										/>
									)}
									{phase === 'finishing' && (
										<Button
											title={t('common.cancel')}
											type="transparent"
											small
											onClick={stopFinishing}
										/>
									)}
									{isStopped && (
										<Button title={t('common.dismiss')} type="transparent" small onClick={close} />
									)}
								</>
							)}
						</FlexGap>
					</FlexGap>
				</BannerWrapper>
			)}
		</AnimatePresence>
	);
}
